import { autoinject, bindable } from 'aurelia-framework';
import { computedFrom } from 'aurelia-framework';
import { Device } from "../../../models/device";
import { applicationLogger } from "../../../logging/logger";
import { TemperatureBaseDevice } from "./temperature-base-device";
import { Store } from "aurelia-store";
import * as State from "../../../store/state";
import IState = State.IState;
import { Api } from "../../../services/api";
import { I18N } from "aurelia-i18n";
import { BaseDevice } from "./base-device";
import { MeasurementSystems } from '../../../converters/measurement-systems';
import { PressureConverter } from "../../../converters/pressure-value";
import { MdTooltip } from "aurelia-materialize-bridge";

@autoinject
export class MilesitePipePressure extends BaseDevice {
	@bindable humidity;
	i18n: I18N;
	tooltip: MdTooltip;

	constructor(store: Store<IState>, api: Api, i18n: I18N ) {
		super(store, api);
		this.i18n = i18n;
	}

	activate(device: Device) {
		applicationLogger.debug(`${MilesitePipePressure.name}.activate() called.`);
		this.device = device;

	
	}

	attached() {
		if (this.tooltip) {
			this.tooltip.html = this.buildToolTip();
		}
	}

	@computedFrom('device.styles')
	get pressureStyle(): string {
		return this.createPressureStyle(this.device.styles);
	}

	createPressureStyle(styles: any): string {
		if (typeof styles !== "undefined" && typeof styles.pressure !== "undefined") {
			return `${styles.pressure}-text`;
		}
		return "grey-text";
	}

	getMeasurementSystem() {
		let system = localStorage.getItem("measurement-system");
		if (!system) {
			system = MeasurementSystems.imperial;
		}
		return system;
	}

	getPressureUnits(): string {
		let system = this.getMeasurementSystem();
		switch (system) {
			case MeasurementSystems.imperial:
				return "psi";
			case MeasurementSystems.metric:
				return "kPa";
			default:
				return "psi";
		}
	}

	convertPressure(pressure: number) {
		let system = this.getMeasurementSystem();
		switch (system) {
			case MeasurementSystems.metric:
				return pressure;
			case MeasurementSystems.imperial:
				return this.round(PressureConverter.toImperial(pressure), 1);
			default:
				return this.round(PressureConverter.toImperial(pressure), 1);
		}
	}

	round(value: number, precision: number): number {
		const multiplier = Math.pow(10, precision || 0);
		return Math.round(value * multiplier) / multiplier;
	}

	buildToolTip(): string {

		if (!this.device && !this.device.properties && !this.device.properties.pressure && !this.device.properties.pressure.value) {

			applicationLogger.debug(`First Check: The 'device' ${this.device.name} does not have a pipe pressure rule, cannot build tooltip.`);
			return null;
		}

		if (this.device.properties.pressure === undefined ||
			this.device.properties.pressure.value === undefined) {
			applicationLogger.debug(`Second Check: The 'device' ${this.device.name} does not have a pipe pressure rule, cannot build tooltip.`);
			return null;
		}

		const data = this.device.properties.pressure.value.split(",");
		var recommendedMinimumLabel = this.i18n.tr('recommended-minimum');
		var requiredMinimumLabel = this.i18n.tr('required-minimum');
		var requiredMaximumLabel = this.i18n.tr('required-maximum');
		var recommendedMaximumLabel = this.i18n.tr('recommended-maximum');

		var recommendedMinimum = this.convertPressure(data[0]);
		var requiredMinimum = this.convertPressure(data[1]);
		var requiredMaximum = this.convertPressure(data[2]);
		var recommendedMaximum = this.convertPressure(data[3]);

		var html = `<div class="card inner-card">`;
		html += `<div class="card-header grey-text center-block"><span class="text-center" > ${this.i18n.tr("pressure-rule-2")} (${this.getPressureUnits()})</span></div>`;
		html += `<div class="card-content valign center-block">`;
		html += `${this.buildLabel(recommendedMinimumLabel, recommendedMinimum, 'label-1', 'red-label')}`;
		html += `${this.buildLabel(requiredMinimumLabel, requiredMinimum, 'label-2', 'yellow-label')}`;
		html += `${this.buildLabel(requiredMaximumLabel, requiredMaximum, 'label-3', 'yellow-label')}`;
		html += `${this.buildLabel(recommendedMaximumLabel, recommendedMaximum, 'label-4', 'red-label')}`;
		html += `</div>`;
		html += `</div>`;
		return html;
	}

	buildLabel(label: string, value: number, id: string, htmlClass: string): string {

		var html = `<div class="input-field inline">`;
		html += `<input value="${value}" readonly="true" disabled="disabled"  id ="${id}" type="text" placeholder="${label}" > `;
		html += `<label class="active ${htmlClass}" for="${id}">${label}</label>`;
		html += `</div>`;
		return html;
	}

}